import { useOrganizationContext } from '@/contexts/organization-context';
import { InputGroup } from '../../input-group';
import SettingsTabTitle from '../settings-tab-title';
import { useMutation, useQuery } from '@apollo/client';
import {
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/shadcn/dropdown-menu';
import { useEffect, useState, useMemo } from 'react';
import { gql } from '@/__generated__';
import {
  FrequencyUnit,
  OrganizationGeoLocationV2,
  OrganizationGetByIdSuccess,
} from '@/__generated__/graphql';
import { logError } from '@/lib/rum';
import { GrantSettingsDropdown } from './grant-settings-dropdown';
import { Spinner } from '@/components/basic/spinner';
import { Button } from '@/components/shadcn/button';
import { useToast } from '@/components/shadcn/use-toast';
import { ServedLocations } from './grant-settings-served-locations';
import GrantWritingSettings from './grant-writing-settings';
import { handleOnlyNumber } from '@/lib/utils';

const FetchGrantSettingsQuery = gql(`
  query FetchGrantSettings($input: OrganizationGetByIdInput!) {
    organization {
      getById(input: $input) {
        ... on OrganizationGetByIdSuccess {
          result {
            id
            grantSettings {
              id
              grantApplicant {
                name
              }
              grantFrequency {
                frequencyUnit
                frequencyValue
              }
              numberOfGrants
              grantWriting
              grantWritingCredit
              locationConnectionV2 {
              edges {
                node {
                  id
                  locationName
                }
              }
            }
            }
          }
        }
      }
    }
  }
`);

export const UpsertGrantSettingsMutation = gql(`
  mutation UpsertGrantSettings($input: OrganizationGrantSettingsInput!) {
    grantSettings {
      upsert(input: $input) {
        ... on OrganizationGrantSettingsResult {
          result {
            id
            grantFrequency {
              frequencyUnit
              frequencyValue
            }
            numberOfGrants
            grantWriting
            grantWritingCredit
          }
        }
      }
    }
  }
`);

export function GrantsTab() {
  const { organizationId } = useOrganizationContext();

  const { loading: loadingSettingsQuery, data } = useQuery(
    FetchGrantSettingsQuery,
    {
      onError(error) {
        logError(
          'Something went wrong while loading organization grant settings',
          error
        );
        toast({ description: 'Failed to load grant settings' });
      },
      variables: {
        input: {
          id: organizationId as string,
        },
      },
      skip: !organizationId,
    }
  );

  const grantSettings = useMemo(() => {
    return (data?.organization.getById as OrganizationGetByIdSuccess)?.result
      .grantSettings;
  }, [data]);

  const [upsertGrantSettings, { loading: loadingSettingsMutation }] =
    useMutation(UpsertGrantSettingsMutation, {
      refetchQueries: ['FetchGrantSettings'],
    });

  const [frequencyValue, setFrequencyValue] = useState<number>();
  const [numberOfGrants, setNumberOfGrants] = useState<number>();
  const [applicantName, setApplicantName] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);

  const { toast } = useToast();

  const handleCreateDefaultSettings = async () => {
    if (!organizationId) return;

    upsertGrantSettings({
      variables: {
        input: {
          organizationId,
          grantFrequency: {
            frequencyUnit: FrequencyUnit.Week,
            frequencyValue: 2,
          },
          numberOfGrants: 2,
        },
      },
      refetchQueries: ['FetchGrantSettings'],
      onError(error) {
        logError(
          `Failed to create grant settings for organizationId ${organizationId}`,
          error
        );
        toast({
          description: 'Could not create settings',
          variant: 'destructive',
          duration: 5000,
        });
      },
    });
  };

  const handleFrequencyUnitClick = (unit: FrequencyUnit) => {
    upsertGrantSettings({
      variables: {
        input: {
          organizationId: organizationId as string,
          grantFrequency: {
            frequencyUnit: unit,
            frequencyValue: grantSettings?.grantFrequency
              .frequencyValue as number,
          },
        },
      },
    })
      .then(() =>
        toast({
          description: 'Frequency unit updated successfully',
        })
      )
      .catch((e) => {
        toast({
          variant: 'destructive',
          description: 'Failed to update frequency unit',
        });
        logError('Failed to update grant settings frequency unit', e);
      });
  };

  const handleFrequencyValueBlur = (newVal: number) => {
    if (newVal === grantSettings?.grantFrequency.frequencyValue) return;

    upsertGrantSettings({
      variables: {
        input: {
          organizationId: organizationId as string,
          grantFrequency: {
            frequencyUnit: grantSettings?.grantFrequency
              .frequencyUnit as FrequencyUnit,
            frequencyValue: newVal as number,
          },
        },
      },
    })
      .then(() =>
        toast({
          description: 'Frequency value updated successfully',
        })
      )
      .catch((e) => {
        toast({
          variant: 'destructive',
          description: 'Failed to update frequency value',
        });
        logError('Failed to update grant settings frequency value', e);
      });
  };

  const handleNumberOfGrantBlur = (newVal: number) => {
    if (numberOfGrants === grantSettings?.numberOfGrants) return;

    upsertGrantSettings({
      variables: {
        input: {
          organizationId: organizationId as string,
          numberOfGrants: newVal,
        },
      },
    })
      .then(() =>
        toast({
          description: 'Number of grants updated successfully',
        })
      )
      .catch((e) => {
        toast({
          variant: 'destructive',
          description: 'Failed to update number of grants',
        });
        logError('Failed to update grant settings number of grants', e);
      });
  };

  const handleGrantApplicantBlur = (key: string, newVal: string) => {
    const fieldToCheck =
      key === 'name'
        ? grantSettings?.grantApplicant?.name
        : grantSettings?.grantApplicant?.position;

    if (newVal === '' && fieldToCheck === '') {
      return;
    }

    upsertGrantSettings({
      variables: {
        input: {
          organizationId: organizationId as string,
          grantApplicant: {
            [key]: newVal,
          },
        },
      },
    })
      .then(() =>
        toast({
          description: 'Grant applicant updated successfully',
        })
      )
      .catch((e) => {
        toast({
          variant: 'destructive',
          description: 'Failed to update grant applicant',
        });
        logError('Failed to update grant settings grant applicant', e);
      });
  };

  useEffect(() => {
    setFrequencyValue(grantSettings?.grantFrequency.frequencyValue);
    setNumberOfGrants(grantSettings?.numberOfGrants);
    setApplicantName(grantSettings?.grantApplicant?.name || '');
  }, [grantSettings]);

  if (!organizationId) {
    return (
      <div>
        <SettingsTabTitle title='Grants' />
        <p>Please select an organization first.</p>
      </div>
    );
  }

  if (!grantSettings && !loadingSettingsQuery && !loadingSettingsMutation) {
    return (
      <>
        <div>
          <SettingsTabTitle title='Grants' />
          <p>
            It looks like the organization does not have grant settings
            configured.
          </p>
          <p>To create default settings, please click Create.</p>
        </div>
        <div>
          <Button className='mt-10' onClick={handleCreateDefaultSettings}>
            Create
          </Button>
        </div>
      </>
    );
  }

  return (
    <div>
      <SettingsTabTitle title='Grant' />
      {(loadingSettingsMutation || loadingSettingsQuery || loading) && (
        <div className='absolute left-0 top-0 h-full w-full bg-gray-400 opacity-40'>
          <Spinner />
        </div>
      )}
      <div className='flex flex-col gap-4'>
        <label className='flex items-center text-gray-settings'>
          Frequency Unit
          <span className='group relative ml-2 flex items-center'>
            <span className='cursor-pointer text-gray-400'>?</span>
            <div className='absolute left-full top-1/2 ml-2 w-80 -translate-y-1/2 transform rounded-lg bg-transparent p-2 text-sm text-gray-400 opacity-0 transition-opacity group-hover:opacity-100'>
              Describes how often and how many grants should be sent to the
              organization. Here: send {grantSettings?.numberOfGrants}{' '}
              {(grantSettings?.numberOfGrants as number) > 1
                ? 'grants'
                : 'grant'}{' '}
              every {grantSettings?.grantFrequency?.frequencyValue}{' '}
              {grantSettings?.grantFrequency?.frequencyUnit?.toLowerCase()}
              {(grantSettings?.grantFrequency?.frequencyValue as number) > 1 &&
                's'}
              .
            </div>
          </span>
        </label>
        <div>
          <DropdownMenu>
            <DropdownMenuTrigger>
              <GrantSettingsDropdown
                value={
                  grantSettings?.grantFrequency.frequencyUnit ||
                  'Select Frequency Unit'
                }
              >
                {Object.values(FrequencyUnit)
                  .filter(
                    (u) => u !== grantSettings?.grantFrequency.frequencyUnit
                  )
                  .map((unit) => (
                    <DropdownMenuItem
                      key={unit}
                      onClick={() => handleFrequencyUnitClick(unit)}
                    >
                      {unit}
                    </DropdownMenuItem>
                  ))}
              </GrantSettingsDropdown>
            </DropdownMenuTrigger>
          </DropdownMenu>
        </div>
        <InputGroup
          labelTitle='Frequency Value'
          placeholder='Frequency Value'
          value={frequencyValue}
          onChange={(e) => setFrequencyValue(Number(e.target.value))}
          onKeyDown={handleOnlyNumber}
          min={1}
          max={10}
          onBlur={(e) => handleFrequencyValueBlur(Number(e.target.value))}
        />
        <InputGroup
          labelTitle='Number Of Grants'
          placeholder='Number Of Grants'
          onKeyDown={handleOnlyNumber}
          value={numberOfGrants}
          onChange={(e) => setNumberOfGrants(Number(e.target.value))}
          min={1}
          max={10}
          onBlur={(e) => handleNumberOfGrantBlur(Number(e.target.value))}
        />

        <GrantWritingSettings
          grantWriting={grantSettings?.grantWriting}
          grantWritingCredit={grantSettings?.grantWritingCredit}
          setLoading={setLoading}
        />

        <ServedLocations
          setLoading={setLoading}
          locations={
            (grantSettings?.locationConnectionV2.edges.map(
              (edge) => edge.node
            ) as OrganizationGeoLocationV2[]) ?? []
          }
        />

        <div>
          <SettingsTabTitle title='Grant Applicant' />
          <InputGroup
            labelTitle='Name'
            placeholder='Name'
            value={applicantName}
            onChange={(e) => setApplicantName(e.target.value)}
            onBlur={(e) => handleGrantApplicantBlur('name', e.target.value)}
          />
        </div>
      </div>
    </div>
  );
}
